import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { useQuery } from '@tanstack/react-query';
import lodash from 'lodash';
import { useState } from 'react';
import { analyticsService } from '../../../application/AnalyticsService';
import { AnalyticsDriversSurvey } from '../../../domain/analytics/AnalyticsDriversSurvey';
import { VehicleFlat } from '../../../domain/vehicle/VehicleFlat';
import { BinTypes } from '../../../interfaces/analytics';
import useErrorMiddleware from '../../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../project/useRetrieveActiveProject';

import { useRetrieveUser } from '../../user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../user/useRetrieveUserCredentials';
import { UseAnalyticsFiltersResponse } from '../useAnalyticsFilters';

export const DRIVERS_SURVEYS_PAGINATION_LIMIT = 25;

type UseRetrieveAnalyticsDriversSurveysResponse = ServiceResponse<() => void, PaginatedResult<AnalyticsDriversSurvey>>;

// Hooks act as 'Adapter' layer.
export const useRetrieveAnalyticsDriversSurveys = ({
  startDate,
  endDate,
  binType,
  vehicles,
  limit = DRIVERS_SURVEYS_PAGINATION_LIMIT,
  offset = 0,
}: {
  startDate: Date;
  endDate: Date;
  binType: BinTypes; // TODO: REMOVE FROM INTERFACES
  vehicles: Array<VehicleFlat>;
  limit?: number;
  offset?: number;
}): UseRetrieveAnalyticsDriversSurveysResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();

  const [currentParams, setCurrentParams] = useState<
    Omit<UseAnalyticsFiltersResponse, `stackedChart` | `cumulativeChart` | `chartType`>
  >({
    binType,
    startDate,
    endDate,
    vehicles,
  });

  const [surveysResponse, setSurveysResponse] = useState<PaginatedResult<AnalyticsDriversSurvey> | undefined>();

  const {
    isInitialLoading: isLoading,
    isFetching,
    refetch,
  } = useQuery(
    [`drivers_analytics_surveys`, activeProject!.id, startDate, endDate, binType, vehicles, limit, offset],
    () =>
      analyticsService.getAnalyticsDriversSurveys({
        startDate,
        endDate,
        vehicleIds: vehicles.map((vehicle) => vehicle.id),
        projectId: activeProject!.id,
        limit,
        offset,
      }),
    {
      enabled: !!user && !!activeProject && userCredentials?.subscription.test(`planner.analytics`),
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (response: PaginatedResult<AnalyticsDriversSurvey>) => {
        if (
          !lodash.isEqual(currentParams, {
            binType,
            startDate,
            endDate,
            vehicles,
          })
        ) {
          setSurveysResponse(response);
          setCurrentParams({
            binType,
            startDate,
            endDate,
            vehicles,
          });
        } else {
          setSurveysResponse({
            ...response,
            docs: (surveysResponse?.docs ?? []).concat(response.docs ?? []),
          });
        }
      },
      onError: (err: any) => {
        console.info(`Drivers analytics time on route error`, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
      },
    }
  );

  return {
    error: false,
    isLoading: isLoading || isFetching,
    success: false,
    run: refetch,
    data: surveysResponse || {
      docs: [],
      total: 0,
      limit: DRIVERS_SURVEYS_PAGINATION_LIMIT,
    },
  };
};
